import { useGetUnreadCount } from '@/api';
import logoImage from '@/assets/images/logo.png';
import { useScrollContainer, DESKTOP_NAV_RAIL_WIDTH } from '@/components/layout';
import { useIsQuizTaking } from '@/components/nav/useNavGateBehaviors';
import { useIsDesktop } from '@/hooks/useIsDesktop';
import { useIsRealtimeActive } from '@/lib/ably';
import { selectCurrentUser, selectIsLoggedIn } from '@/store/slices/authSlice';
import { glassCardProps } from '@/styles/glassCard';
import { Avatar, Box, Button, Flex, HStack, IconButton, Image } from '@chakra-ui/react';
import { useNavigate } from '@tanstack/react-router';
import { Bell, Map, List, Users, HelpCircle } from 'lucide-react';
import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

interface HeaderProps {
  page: string;
  onPageViewSwitch?: () => void;
  onHintsClick?: () => void;
  alwaysVisible?: boolean;
  disableTransparency?: boolean;
  hideIcons?: boolean;
  children?: React.ReactNode;
}

export default function AppHeader(props: HeaderProps) {
  const navigate = useNavigate();
  const isDesktop = useIsDesktop();
  const isLoggedIn = useSelector(selectIsLoggedIn);
  const currentUser = useSelector(selectCurrentUser);
  // DesktopNavRail renders `null` while a quiz is being taken (see
  // TabLayout.tsx's reservesRailGutter for the same guard) — without this,
  // the header kept reserving DESKTOP_NAV_RAIL_WIDTH for a rail that isn't
  // there, leaving the top-left corner uncovered and exposing
  // GradientBackground's purple orb as a bleed-through strip.
  const isQuizTaking = useIsQuizTaking();

  // D61 Phase 2 — chrome-less mode: at `lg`+, for a logged-in member not
  // taking a quiz, AppHeader renders only its children (the filter/tab bar
  // it wraps) — no icon row, no logo. The filter bar never changes parent
  // (it stays AppHeader's child at every breakpoint, always), so nothing
  // ever unmounts/remounts across a breakpoint crossing — see the D61 plan's
  // Feasibility Audit (FR-B12) for why two earlier designs that DID reparent
  // the bar were rejected. `useIsDesktop()` (not a bare `useBreakpointValue`)
  // is required here — see that hook's own docblock for the SSR-mismatch
  // flash it prevents.
  const isChromeless = isDesktop && isLoggedIn && !isQuizTaking;

  // Hide-on-scroll-down, show-on-scroll-up logic (mobile only)
  const { scrollContainerRef } = useScrollContainer();
  const [isHeaderVisible, setIsHeaderVisible] = useState(true);
  const lastScrollY = useRef(0);
  const ticking = useRef(false);

  const handleScroll = useCallback(() => {
    const scrollContainer = scrollContainerRef?.current;
    if (!scrollContainer) return;

    const currentScrollY = scrollContainer.scrollTop;
    const scrollDelta = currentScrollY - lastScrollY.current;

    // Always show header when near top of page
    if (currentScrollY < 10) {
      setIsHeaderVisible(true);
    }
    // Scrolling down - hide header (only if scrolled past threshold)
    else if (scrollDelta > 5 && currentScrollY > 60) {
      setIsHeaderVisible(false);
    }
    // Scrolling up - show header immediately
    else if (scrollDelta < -5) {
      setIsHeaderVisible(true);
    }

    lastScrollY.current = currentScrollY;
    ticking.current = false;
  }, [scrollContainerRef]);

  useEffect(() => {
    // Desktop's transform is permanently inert (`isDesktop` short-circuits
    // the translateY expression below to '0' unconditionally) — attaching
    // the listener there buys nothing and re-renders AppHeader and its
    // children (the filter bar, and its dropdown) on every scroll frame.
    if (isDesktop) return;

    const scrollContainer = scrollContainerRef?.current;
    if (!scrollContainer) return;

    const onScroll = () => {
      if (!ticking.current) {
        window.requestAnimationFrame(handleScroll);
        ticking.current = true;
      }
    };

    scrollContainer.addEventListener('scroll', onScroll, { passive: true });
    return () => scrollContainer.removeEventListener('scroll', onScroll);
  }, [handleScroll, scrollContainerRef, isDesktop]);

  // D59 Phase 1 — measure this header's own rendered (border-box, safe-area
  // inset included) height and publish it as --app-header-height on
  // <html>, so PageWrapper's headerOffsetCss() (see PageWrapper.tsx) can
  // offset content by the header's REAL height instead of a static
  // per-breakpoint estimate. The published value already includes the
  // safe-area inset (this Box's own pt="env(safe-area-inset-top)"), so
  // consumers must NOT add the inset again on top of it.
  const headerBoxRef = useRef<HTMLDivElement>(null);

  // D61 Phase 2 — the measurement effect below is a useLayoutEffect with []
  // deps standing up a single ResizeObserver for AppHeader's whole mounted
  // lifetime. isChromeless must reach publish() through a ref updated every
  // render, never as a dependency of that effect — adding it as a dependency
  // would tear down and recreate the observer on every breakpoint flip
  // instead of just changing what the existing observer's callback publishes.
  const isChromelessRef = useRef(isChromeless);
  useEffect(() => {
    isChromelessRef.current = isChromeless;
  });

  useLayoutEffect(() => {
    const el = headerBoxRef.current;
    if (!el) return;

    const publish = (height: number) => {
      if (!el.isConnected) return;
      // A hidden/detached element measures 0 — in chrome mode that can only
      // mean "not yet laid out," so a 0 there is rejected to protect the
      // previously published real height. In chrome-less mode, 0 is a
      // legitimate terminal state (a childless page like the dashboard) —
      // a documented D59 contract change, see Fixed Header Offset.md.
      if (!height && !isChromelessRef.current) return;
      document.documentElement.style.setProperty('--app-header-height', `${height}px`);
    };

    // Synchronous initial measurement so the first paint is already
    // correct — do not wait for the observer's first callback.
    publish(el.getBoundingClientRect().height);

    let ro: ResizeObserver | undefined;
    // ResizeObserver is Baseline Widely Available since 2020 and present
    // in Capacitor's iOS/Android WebViews. This guard is defense-in-depth
    // for an unsupported engine, not an expected runtime path — on that
    // path the header falls back to a one-time initial measurement with
    // no live updates.
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(() => {
        // Re-measure via the SAME getBoundingClientRect() call used above
        // rather than reading the observer entry's borderBoxSize — one
        // measurement method everywhere avoids the two APIs disagreeing
        // by a sub-pixel rounding difference.
        publish(el.getBoundingClientRect().height);
      });
      // Explicit border-box: the published value is getBoundingClientRect()'s
      // border-box height, so observing border-box (not the content-box
      // default) keeps the observed box and the measured box in exact sync.
      ro.observe(el, { box: 'border-box' });
    }

    return () => {
      ro?.disconnect();
      // Clear so a page that next mounts without AppHeader (e.g.
      // OnboardingFindFriends) doesn't inherit a stale value. Routes are
      // code-split (React.lazy + Suspense) — for an uncached route this
      // unmount and the next page's mount effects can land in different
      // commits, not guaranteed the same one. That gap resolves to
      // headerOffsetCss's fallback branch (today's static estimate) for
      // at most one frame, which is harmless — this is not a same-commit
      // guarantee, just a safe worst case.
      document.documentElement.style.removeProperty('--app-header-height');
    };
  }, []);

  // Check if real-time is active - falls back to polling when not connected
  const isRealtimeActive = useIsRealtimeActive();

  const { data: unreadData } = useGetUnreadCount({
    enabled: true,
    // Use polling as fallback when real-time is not active
    refetchInterval: isRealtimeActive ? undefined : 30000,
  });

  const searchPageAliases = ['search', 'map'];
  const isSearchPage = searchPageAliases.some(alias => alias === props.page);
  const isListsPage = props.page === 'lists';

  const notificationIcon = () => (
    <Box position="relative">
      <IconButton
        aria-label="Notifications"
        variant="ghost"
        // Intentionally literal: sits directly on the persistent glass header bar, which stays dark in both app modes
        color="white"
        _hover={{ bg: 'whiteAlpha.200' }}
        fontSize={{ base: '1.25rem', lg: '1.25rem' }}
        minW="44px"
        minH="44px"
        onClick={() => navigate({ to: '/notifications' })}
      >
        <Bell />
      </IconButton>
      {unreadData && unreadData.unreadCount > 0 && (
        <Box
          position="absolute"
          top={{ base: '-4px', lg: '-2px' }}
          right={{ base: '-4px', lg: '-2px' }}
          bg="red.500"
          // Intentionally literal: text on a fixed red.500 badge, not a card surface — needs contrast against that saturated color regardless of app mode
          color="white"
          borderRadius="full"
          minW={{ base: '18px', lg: '20px' }}
          h={{ base: '18px', lg: '20px' }}
          display="flex"
          alignItems="center"
          justifyContent="center"
          fontSize="xs"
          fontWeight="bold"
        >
          {unreadData.unreadCount > 99 ? '99+' : unreadData.unreadCount}
        </Box>
      )}
    </Box>
  )

  const friendsIcon = () => (
    <IconButton
      aria-label="Friends"
      variant="ghost"
      // Intentionally literal: sits directly on the persistent glass header bar, which stays dark in both app modes
      color="white"
      _hover={{ bg: 'whiteAlpha.200' }}
      fontSize={{ base: '1.25rem', lg: '1.25rem' }}
      minW="44px"
      minH="44px"
      onClick={() => navigate({ to: '/friends' })}
    >
      <Users />
    </IconButton>
  )

  const hintsIcon = () => (
    <IconButton
      aria-label="Tips & Gestures"
      variant="ghost"
      // Intentionally literal: sits directly on the persistent glass header bar, which stays dark in both app modes
      color="white"
      _hover={{ bg: 'whiteAlpha.200' }}
      fontSize={{ base: '1.25rem', lg: '1.25rem' }}
      minW="44px"
      minH="44px"
      onClick={props.onHintsClick}
    >
      <HelpCircle />
    </IconButton>
  )

  // New avatar tap target (D45b Phase 2 / D4) — resolved mobile+desktop path
  // to Profile. Sized to match the existing 44x44 icon-button convention
  // used by notificationIcon/friendsIcon in this same cluster.
  const avatarButton = () => (
    <Box
      as="button"
      aria-label="Your profile"
      onClick={() => navigate({ to: '/profile' })}
      // Round-2 fix: hidden at `lg`+ — the desktop rail's bottom-pinned
      // profile peek (DesktopNavRail.tsx) already routes to /profile and
      // carries the `⋯` menu trigger, so this header avatar became a
      // redundant duplicate once the rail shipped. Still renders at `base`
      // (mobile has no rail).
      display={{ base: 'flex', lg: 'none' }}
      alignItems="center"
      justifyContent="center"
      minW="44px"
      minH="44px"
      bg="transparent"
      border="none"
      outline="none"
      borderRadius="full"
      _hover={{ bg: 'whiteAlpha.200' }}
      // D45b Phase 6 a11y sweep: outline="none" above (needed so the plain
      // <button> reset doesn't draw its own default outline) left this
      // control with NO visible focus indicator at all when tabbed to —
      // found live via keyboard walkthrough. Restores a visible ring,
      // matching the whiteAlpha focus-ring convention used elsewhere on
      // this same dark glass header (e.g. PillWithDetail.tsx).
      _focusVisible={{ boxShadow: '0 0 0 2px var(--color-overlay-90), 0 0 0 4px var(--color-primary-purple)' }}
      sx={{ WebkitTapHighlightColor: 'transparent' }}
    >
      {/* size="xs" (24px) matches lucide's fixed 24px icon glyphs (icons don't
          inherit fontSize — see AppHeader Phase 2 design review); a thin
          border offsets the solid-fill-vs-stroke-icon weight mismatch. */}
      <Avatar
        size="xs"
        name={currentUser?.name}
        src={currentUser?.photoUrl || undefined}
        borderWidth="1.5px"
        borderColor="whiteAlpha.500"
      />
    </Box>
  )

  const mapMenuToggleIcon = () => (
    <IconButton
      aria-label="Toggle View"
      variant="ghost"
      // Intentionally literal: sits directly on the persistent glass header bar, which stays dark in both app modes
      color="white"
      _hover={{ bg: 'whiteAlpha.200' }}
      fontSize={{ base: '1.25rem', lg: '1.25rem' }}
      minW="44px"
      minH="44px"
      onClick={props.onPageViewSwitch}
    >
      {props.page === 'search' ? <Map /> : <List />}
    </IconButton>
  )

  // D61 Phase 2 (Feasibility Audit, Change 1 / round-3 finding S2) — the
  // glass surface is conditional on whether there are children, not on
  // chrome-less mode itself: present (unconditionally, exactly as before)
  // whenever there are children, since the search/lists filter bars carry no
  // background of their own. Only a chrome-less page with NO children (e.g.
  // the dashboard) drops it, so the box measures exactly 0px on every
  // device, including a notched tablet's safe-area inset — never by
  // returning null, which would unmount the component and fall back to the
  // D59 static height estimate for every consumer.
  const hasChildren = !!props.children;
  const dropGlassSurface = isChromeless && !hasChildren;

  return (
    <>
      {/* Header content - slides up/down */}
      <Box
        ref={headerBoxRef}
        data-testid="app-header"
        position="fixed"
        top="0"
        left={{ base: 0, lg: isQuizTaking ? 0 : DESKTOP_NAV_RAIL_WIDTH }}
        right="0"
        zIndex="sticky"
        // bg intentionally stays var(--color-overlay-40) (a dark scrim), not
        // glassCardProps.bg — AppHeader sits fixed over photo hero content on
        // many pages and needs real darkening for icon/text legibility, unlike
        // the roster's other nav-bar members which sit over the app's own
        // dark background. The blur/saturate/brightness *recipe* is still the
        // shared roster value (D45b Phase 6, FR-N9) — only the tint differs.
        bg={dropGlassSurface ? undefined : (props.disableTransparency ? 'black' : 'var(--color-overlay-40)')}
        backdropFilter={dropGlassSurface ? undefined : glassCardProps.backdropFilter}
        sx={dropGlassSurface ? undefined : {
          WebkitBackdropFilter: glassCardProps.backdropFilter,
          '@supports not (backdrop-filter: blur(1px))': {
            bg: props.disableTransparency ? 'black' : 'blackAlpha.900',
            backdropFilter: 'none',
          },
        }}
        pt={dropGlassSurface ? '0' : 'env(safe-area-inset-top)'}
        transform={isHeaderVisible || props.alwaysVisible || isDesktop ? 'translateY(0)' : 'translateY(-100%)'}
        transition="transform 0.3s ease-in-out"
      >
        {!isChromeless && (
          <Flex
            as="nav"
            align="center"
            // `flex-end` packs the icon cluster against the right edge on the
            // normal desktop path (logo lives in DesktopNavRail there, so this
            // Flex has only the icons as a child). F1 fix: on quiz-taking
            // routes `hideIcons` is set and the rail is unmounted, so the
            // now-visible logo above is this Flex's ONLY child — `flex-end`
            // would push it to the right edge instead of the left, unlike
            // every other desktop-quiz surface. `hideIcons` is used
            // exclusively by QuizTaking.tsx (verified), so this doesn't affect
            // any other page's icon-cluster alignment.
            justify={{ base: 'space-between', lg: props.hideIcons ? 'flex-start' : 'flex-end' }}
            wrap="wrap"
            pl={{ base: 4, lg: 6 }}
            py={{ base: 3, lg: 4 }}
            boxShadow={props.children ? 'none' : 'lg'}
          >
            {/* Paire Logo — desktop (`lg`+) moves this into DesktopNavRail's
                fixed left rail instead; hidden here via responsive `display`
                rather than by a breakpoint branch of its own. (The whole icon
                row around it IS conditional now — D61's chrome-less mode
                drops it at `lg`+ for a logged-in member — but that gate is
                driven by `useIsDesktop()`, which resolves correctly on the
                first client render; the logo's own visibility stays pure
                CSS.) F1 fix:
                DesktopNavRail renders `null` while a quiz is being taken (same
                `isQuizTaking` guard read above for the header's own `left`
                offset), so on quiz routes there is no rail to carry the logo —
                keep this copy visible at `lg`+ in that case, or desktop quiz
                routes render an empty header bar with no logo anywhere. */}
            <Flex align="center" mr={{ base: 2, lg: 5 }} display={{ base: 'flex', lg: isQuizTaking ? 'flex' : 'none' }}>
              <Image
                src={logoImage}
                height={{ base: '48px', lg: '64px' }}
                objectFit='contain'
                data-testid="app-header-logo"
              ></Image>
            </Flex>

            {/* Icons or Auth Buttons */}
            {!props.hideIcons && (
              isLoggedIn ? (
                <HStack spacing={{ base: 0.5, lg: 2 }} mr={{ base: 4, lg: 0 }}>
                  {/* D61 Phase 6: Quick Search magnifier retired — the unified
                      search box on /search itself is the only entry point now. */}
                  {isSearchPage && mapMenuToggleIcon()}
                  {isListsPage && (
                    <>
                      {hintsIcon()}
                    </>
                  )}
                  {notificationIcon()}
                  {friendsIcon()}
                  {avatarButton()}
                </HStack>
              ) : (
                <HStack spacing={2} mr={{ base: 4, lg: 6 }}>
                  <Button
                    variant="ghost"
                    // Intentionally literal: sits directly on the persistent glass header bar, which stays dark in both app modes
                    color="white"
                    size="sm"
                    _hover={{ bg: 'whiteAlpha.200' }}
                    onClick={() => navigate({ to: '/login', search: { form: 'login' } })}
                  >
                    Log In
                  </Button>
                  <Button
                    bg="purple.500"
                    // Intentionally literal: text on a fixed purple.500 button, not a card surface — needs contrast against that saturated color regardless of app mode
                    color="white"
                    size="sm"
                    _hover={{ bg: 'purple.600' }}
                    onClick={() => navigate({ to: '/login', search: { form: 'signup' } })}
                  >
                    Sign Up
                  </Button>
                </HStack>
              )
            )}
          </Flex>
        )}
        {props.children}
      </Box>
    </>
  );
}
